import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { updateUserProfile } from "@endpoints/auth.service";
import { ProfileProgressContext } from "@pages/Layout/provider/CreateProfileProvider";
import { setUserData } from "@store/actions/auth.action";
import { RootState } from "@store/reducers";
import { Check, ChevronDown } from "lucide-react";
import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const CreateProfileTracker = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { tabs, activeTab } = useContext(ProfileProgressContext);

  const [isUpdating, setIsUpdating] = useState(false);

  const completedCount =
    tabs?.filter((t: { isCompleted?: boolean }) => t?.isCompleted)?.length ?? 0;
  const progress = tabs?.length
    ? Math.round((completedCount / tabs.length) * 100)
    : 0;

  const isEligibleForDashboard =
    (user?.experiences && user?.experiences?.length) ||
    (user?.educations && user?.educations?.length) ||
    (user?.certifications && user?.certifications?.length);

  const handleContinueToDashboard = async () => {
    try {
      setIsUpdating(true);
      const { success } = await updateUserProfile({
        isCandidateProfileCompleted: true,
      });
      if (success) {
        dispatch(setUserData({ isCandidateProfileCompleted: true }));
        navigate("/");
      }
    } catch (error) {
      console.error("Error continuing to the dashboard", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div
      className={`xl:static top-0 left-0 h-full bg-white z-10 transition-all duration-300 ease-in-out w-16 xl:w-60 pt-4 pr-4 pl-2 min-h-0 flex flex-col`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <div className="flex flex-col gap-2 flex-1">
            <span className="font-semibold leading-5">
              Recommended next steps
            </span>
            <span className="font-medium text-xss text-light-black leading-3.5">
              Continue building your candidate profile
            </span>
          </div>
          <span className="bg-green-50 text-green-700 rounded-full font-semibold text-xss px-1.5">
            {progress}%
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mt-1">
          <div
            className="h-full bg-gradient-to-r from-primary via-primary/80 to-green-500 rounded-full transition-all duration-700 ease-out shadow-sm"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col flex-1 gap-3 mt-5 min-h-0 overflow-y-scroll no-scrollbar pb-10">
        {tabs?.map((tab, tabIndex) => {
          const isOpen = activeTab === tab?.value;
          return (
            <div
              className={`flex flex-col border rounded-md ${tab?.isCompleted ? "bg-green-50" : "bg-white"} p-3 transition-all duration-500`}
            >
              <div className="flex gap-3">
                {tab?.isCompleted ? (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-200">
                    <Check size={10} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                    {tabIndex + 1}
                  </div>
                )}
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">{tab?.label}</span>
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-white">
                      <ChevronDown
                        size={18}
                        className={`transition-transform duration-500 ease-in-out ${isOpen ? "rotate-180" : "rotate-0"}`}
                      />
                    </div>
                  </div>
                  <span className="text-xss font-medium text-light-black">
                    {tab?.subHeading}
                  </span>
                </div>
              </div>
              <div
                className={`grid transition-all duration-500 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0"}`}
              >
                <div className="overflow-hidden">
                  <span className="block text-[10px] font-medium text-light-black p-2">
                    {tab?.description}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {isEligibleForDashboard ? (
          <button
            onClick={handleContinueToDashboard}
            disabled={isUpdating}
            className="flex items-center justify-center gap-2 rounded-md font-semibold text-xs text-white bg-primary hover:bg-primary/90 py-2.5"
          >
            Continue to Dashboard
            {isUpdating ? (
              <LoadingAnimation color="white" className="h-4 w-4" />
            ) : null}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default CreateProfileTracker;
